import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface AuthContextType {
    token: string | null;
    tenantId: string | null;
    username: string | null;
    fullName: string | null;
    isAuthenticated: boolean;
    login: (token: string, tenantId: string | null | undefined, username: string, fullName: string) => void;
    logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
    const [tenantId, setTenantId] = useState<string | null>(() => {
        const stored = localStorage.getItem('tenantId');
        if (stored && stored.trim() !== '') {
            return stored;
        }
        localStorage.removeItem('tenantId');
        return null;
    });
    const [username, setUsername] = useState<string | null>(localStorage.getItem('username'));
    const [fullName, setFullName] = useState<string | null>(localStorage.getItem('fullName'));
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(!!token);

    useEffect(() => {
        // Verifica lo stato di autenticazione all'avvio
        const storedToken = localStorage.getItem('token');
        if (storedToken) {
            setToken(storedToken);
            const storedTenantId = localStorage.getItem('tenantId');
            setTenantId(storedTenantId && storedTenantId.trim() !== '' ? storedTenantId : null);
            setUsername(localStorage.getItem('username'));
            setFullName(localStorage.getItem('fullName'));
            setIsAuthenticated(true);
        }
    }, []);

    const login = (token: string, tenantIdValue: string | null | undefined, username: string, fullName: string) => {
        localStorage.setItem('token', token);
        localStorage.setItem('username', username);
        localStorage.setItem('fullName', fullName || '');

        setToken(token);
        setUsername(username);
        setFullName(fullName);
        setIsAuthenticated(true);

        const normalizedTenant = tenantIdValue && tenantIdValue.trim() !== '' ? tenantIdValue.trim() : null;
        if (normalizedTenant) {
            localStorage.setItem('tenantId', normalizedTenant);
            setTenantId(normalizedTenant);
        } else {
            const existingTenant = localStorage.getItem('tenantId');
            if (existingTenant && existingTenant.trim() !== '') {
                setTenantId(existingTenant.trim());
            } else {
                localStorage.removeItem('tenantId');
                setTenantId(null);
            }
        }
    };

    const logout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('tenantId');
        localStorage.removeItem('username');
        localStorage.removeItem('fullName');

        setToken(null);
        setTenantId(null);
        setUsername(null);
        setFullName(null);
        setIsAuthenticated(false);
    };

    return (
        <AuthContext.Provider value={{
            token,
            tenantId,
            username,
            fullName,
            isAuthenticated,
            login,
            logout
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
